import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createFormatters, type Formatters } from "./format";
import type { MessageKey } from "./keys";
import { en } from "./locales/en";
import { matchLocale, registerMessages, translate, type Locale, type Params } from "./runtime";

registerMessages("en", en);

const STORAGE_KEY = "clubcore.locale";

/** Core Spanish catalog is loaded on demand: only people who switch pay for it. */
async function loadLocale(locale: Locale): Promise<void> {
  if (locale === "es") registerMessages("es", (await import("./locales/es")).es);
}

function storedLocale(): Locale | undefined {
  try {
    return matchLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export interface ClubFormatSettings {
  locale?: string | null | undefined;
  timeZone?: string | null | undefined;
  currency?: string | null | undefined;
}

interface I18nValue {
  /** Language of the interface text. */
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, params?: Params) => string;
  /** Like `t`, but never tagged: for the wording tools themselves, whose own text is not editable on screen. */
  tPlain: (key: MessageKey, params?: Params) => string;
  /** "Edit text on screen" is on: translated strings end with an invisible tag naming their message key. */
  editing: boolean;
  setEditing: (editing: boolean) => void;
  /** Draw again after the wording overrides changed (they live outside React, in the message runtime). */
  refresh: () => void;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? storedLocale() ?? matchLocale(navigator.language) ?? "en");
  const [catalogVersion, bump] = useState(0);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    document.documentElement.lang = locale;
    let live = true;
    void loadLocale(locale).then(() => live && bump((n) => n + 1));
    return () => {
      live = false;
    };
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* the choice lasts for this visit */
    }
  }, []);

  const refresh = useCallback(() => bump((n) => n + 1), []);
  // catalogVersion: a catalog that finishes loading, or wording that changed, must give consumers new strings even though the locale did
  // so it is a dependency of `t` (screens that memoize on `t` draw again too).
  const t = useCallback(
    (key: MessageKey, params?: Params) => {
      void catalogVersion;
      return translate(locale, key, params, editing ? { tag: true } : undefined);
    },
    [locale, catalogVersion, editing],
  );
  const tPlain = useCallback(
    (key: MessageKey, params?: Params) => {
      void catalogVersion;
      return translate(locale, key, params);
    },
    [locale, catalogVersion],
  );
  const value = useMemo(() => ({ locale, setLocale, t, tPlain, editing, setEditing, refresh }), [locale, setLocale, t, tPlain, editing, refresh]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Inside this, every `useT` gives untagged text: the wording tools' dialogs, whose own text "Edit text on screen" must leave alone. */
export function NoTextTags({ children }: { children: ReactNode }) {
  const value = useContext(I18nContext);
  const plain = useMemo(() => (value ? { ...value, t: value.tPlain } : null), [value]);
  return <I18nContext.Provider value={plain}>{children}</I18nContext.Provider>;
}

function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider is missing");
  return value;
}

export const useT = () => useI18n().t;
/** `t` without key tags: for the wording tools' own text, which "Edit text on screen" must leave alone. */
export const usePlainT = () => useI18n().tPlain;
export const useLocale = () => {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
};
/** Edit mode and the redraw after wording changes: for the shell's wording tools only. */
export const useWordingTools = () => {
  const { editing, setEditing, refresh } = useI18n();
  return { editing, setEditing, refresh };
};

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const FormatContext = createContext<Formatters | null>(null);

/**
 * Dates, numbers and money follow the club, not the interface language: a Spanish-speaking
 * employee at a US club still sees US dates and dollars. Without a club (sign-in) the browser's locale applies.
 */
export function FormatProvider({ club, children }: { club?: ClubFormatSettings | null; children: ReactNode }) {
  const formatLocale = club?.locale || navigator.language || "en-US";
  const timeZone = club?.timeZone || browserZone();
  const currency = club?.currency || "USD";
  const format = useMemo(() => createFormatters({ locale: formatLocale, timeZone, currency }), [formatLocale, timeZone, currency]);
  return <FormatContext.Provider value={format}>{children}</FormatContext.Provider>;
}

export function useFormat(): Formatters {
  const value = useContext(FormatContext);
  if (!value) throw new Error("FormatProvider is missing");
  return value;
}
